import { useEffect, useRef, useState } from 'react';
import { useSortableGesture } from './sortable-gesture';
import type { BoardProps } from './board-types';

export function useBoardDrag(props: BoardProps, phone: boolean) {
  const board = useRef<HTMLDivElement>(null);
  const pointer = useRef<{ id: string; x: number; y: number; started: boolean } | null>(null);
  const suppressClick = useRef(false);
  const [target, setTarget] = useState<{ columnId: string; beforeId: string | null } | null>(null);
  const identity = JSON.stringify(props.items.map((item) => [item.id, item.columnId]));
  const drag = useSortableGesture(
    props.items.map((item) => item.id),
    props.controller,
    props.newOperationId,
    (id, position, count) =>
      props.copy.position(props.items.find((item) => item.id === id)!, position, count),
    identity,
  );
  useEffect(() => {
    setTarget(null);
    pointer.current = null;
  }, [identity, props.controller, drag.locked]);
  const allowed = (id: string) => {
    const item = props.items.find((entry) => entry.id === id);
    return !!item && !drag.locked && (props.canMove?.(item) ?? true);
  };
  const begin = (id: string) => {
    if (!allowed(id)) return;
    const item = props.items.find((entry) => entry.id === id)!;
    const rows = props.items.filter((entry) => entry.columnId === item.columnId);
    setTarget({
      columnId: item.columnId,
      beforeId: rows[rows.findIndex((entry) => entry.id === id) + 1]?.id ?? null,
    });
    drag.begin(id);
  };
  const commit = async (id: string, columnId: string, beforeId: string | null) => {
    if (
      !allowed(id) ||
      !props.columns.some((column) => column.id === columnId) ||
      (beforeId !== null &&
        !props.items.some((item) => item.id === beforeId && item.columnId === columnId))
    )
      return;
    const item = props.items.find((entry) => entry.id === id)!;
    const rows = props.items.filter((entry) => entry.columnId === item.columnId);
    const next = rows[rows.findIndex((entry) => entry.id === id) + 1]?.id ?? null;
    if (item.columnId === columnId && next === beforeId) {
      drag.cancel();
      setTarget(null);
      return;
    }
    await drag.commitIntent(id, beforeId, columnId);
    setTarget(null);
  };
  const drop = () => {
    if (drag.gesture && target)
      void commit(drag.gesture.id, target.columnId, target.beforeId).catch(props.onError);
  };
  const point = (x: number, y: number) => {
    if (!drag.gesture) return;
    const element = board.current?.ownerDocument.elementFromPoint(x, y);
    const column = element?.closest<HTMLElement>('.pc-board-col');
    if (!column || !board.current?.contains(column)) return;
    const columnId = column.dataset.columnId!;
    if (phone && columnId !== props.items.find((item) => item.id === drag.gesture?.id)?.columnId)
      return;
    const row = element?.closest<HTMLElement>('.pc-board-card');
    const beforeId = row?.dataset.itemId ?? null;
    if (beforeId !== drag.gesture.id) setTarget({ columnId, beforeId });
  };
  const key = (id: string, event: { key: string; preventDefault(): void }) => {
    if (
      !allowed(id) ||
      ![' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    )
      return;
    event.preventDefault();
    if (event.key === 'Escape') {
      drag.cancel();
      setTarget(null);
    } else if (event.key === ' ') {
      if (drag.gesture?.id === id) drop();
      else begin(id);
    } else if (drag.gesture?.id === id && target) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        if (phone) return;
        const index = props.columns.findIndex((column) => column.id === target.columnId);
        const column = props.columns[index + (event.key === 'ArrowLeft' ? -1 : 1)];
        if (column) setTarget({ columnId: column.id, beforeId: null });
      } else {
        const rows = props.items.filter(
          (item) => item.columnId === target.columnId && item.id !== id,
        );
        const position =
          target.beforeId === null
            ? rows.length
            : rows.findIndex((row) => row.id === target.beforeId);
        const index = Math.max(
          0,
          Math.min(rows.length, position + (event.key === 'ArrowUp' ? -1 : 1)),
        );
        setTarget({ ...target, beforeId: rows[index]?.id ?? null });
      }
    }
  };
  return {
    board,
    drag,
    target,
    allowed,
    key,
    drop,
    commit,
    begin,
    point,
    pointer,
    suppressClick,
    cancel: () => {
      drag.cancel();
      setTarget(null);
      pointer.current = null;
    },
  };
}
